/*
 * FormularioCliente.js — Un solo formulario para Nuevo cliente y Editar cliente (C-026).
 *
 * Etiqueta visible en cada campo, correos como chips, errores junto a su campo con un resumen
 * arriba, pie fijo con Cancelar y Guardar, y "¿Descartar los cambios?" al cerrar con cambios.
 * La lógica pura (validación, correos, sugerencia de mayúsculas) está en validacionCliente.js.
 *
 * props:
 *   modo          'nuevo' | 'editar'
 *   inicial       datos del cliente (los mismos campos que manda/recibe /api/clientes)
 *   responsables  usuarios que pueden llevar clientes
 *   onGuardar     async (datos) => {}; si lanza un error, el formulario sigue abierto
 *   onCerrar      () => {}
 *   zonaFinal     JSX de "Desactivar o eliminar" (solo en Editar, lo arma App.js)
 */
import { useEffect, useRef, useState } from 'react';
import { CircleAlert, X } from 'lucide-react';
import { confirmar } from './Dialogo';
import { BotonIcono } from './Tooltip';
import { OPCIONES_REVISION, normalizarRevision } from './estados';
import {
  agregarCorreos, correoValido, correosAlGuardar, mensajeCorreos, resumenErrores, separarCorreos,
  sugerenciaNombre, sugerirNombre, validarCliente,
} from './validacionCliente';

const REGIMENES = [
  ['resico', 'RESICO'],
  ['general', 'General'],
  ['actividad_empresarial', 'Actividad empresarial'],
  ['arrendamiento', 'Arrendamiento'],
  ['personas_morales', 'Personas morales'],
];
const INDUSTRIAS = [
  ['vinicola', 'Vinícola'],
  ['empresarial', 'Empresarial'],
  ['comercio', 'Comercio'],
  ['servicios', 'Servicios'],
  ['manufactura', 'Manufactura'],
];
// C-021: contabilidad electrónica y la nómina mensual ya no se ofrecen (sus datos no se borran).
const OBLIGACIONES = [
  ['tiene_imss', 'IMSS', 'Tiene empleados. Mensual, vence el 17 del mes siguiente.'],
  ['tiene_180_trimestral', 'Impuesto sobre nómina (1.8%)', 'Trimestral, vence el 17 después de cerrar el trimestre.'],
  ['tiene_hospedaje', 'Impuesto sobre hospedaje', 'Mensual, vence el 17 del mes siguiente.'],
];

// Campo del formulario → id del control que recibe el foco cuando tiene error.
const ID_CAMPO = {
  nombre: 'cf-nombre', contador: 'cf-contador', regimen: 'cf-regimen', industria: 'cf-industria',
  correos: 'cf-correo', telefono: 'cf-telefono',
};

export default function FormularioCliente({ modo, inicial, responsables, onGuardar, onCerrar, zonaFinal }) {
  const editar = modo === 'editar';
  const [datos, setDatos] = useState(() => ({ ...inicial, email: undefined }));
  const [correos, setCorreos] = useState(() => separarCorreos(inicial.email));
  const [correoTexto, setCorreoTexto] = useState('');
  const [errores, setErrores] = useState([]);
  const [sugerencia, setSugerencia] = useState(null);
  const [sucio, setSucio] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const cerrandoRef = useRef(false);

  const cambiar = (campo, valor) => {
    setDatos(d => ({ ...d, [campo]: valor }));
    setSucio(true);
    // Al corregir un campo, su error se va.
    setErrores(e => e.filter(x => x.campo !== campo && !(campo === 'contador_id' && x.campo === 'contador')));
  };
  const cambiarCorreos = (lista) => {
    setCorreos(lista);
    setSucio(true);
    setErrores(e => {
      const sinCorreos = e.filter(x => x.campo !== 'correos');
      const msg = mensajeCorreos(lista);
      return msg ? [...sinCorreos, { campo: 'correos', mensaje: msg, resumen: 'hay un correo que no es válido' }] : sinCorreos;
    });
  };
  const confirmarCorreoEscrito = () => {
    if (!correoTexto.trim()) return correos;
    const lista = agregarCorreos(correos, correoTexto);
    setCorreoTexto('');
    cambiarCorreos(lista);
    return lista;
  };

  const intentarCerrar = async () => {
    if (guardando || cerrandoRef.current) return;
    if (!sucio && !correoTexto.trim()) { onCerrar(); return; }
    cerrandoRef.current = true;
    const descartar = await confirmar({
      nivel: 3,
      titulo: '¿Descartar los cambios?',
      cuerpo: 'Hiciste cambios que todavía no se guardan.',
      accion: 'Descartar cambios',
      cancelar: 'Seguir editando',
    });
    cerrandoRef.current = false;
    if (descartar) onCerrar();
  };

  // Esc también pregunta (si no hay un diálogo encima, que es quien atiende su propio Esc).
  const intentarCerrarRef = useRef(intentarCerrar);
  intentarCerrarRef.current = intentarCerrar;
  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('.dialogo-fondo')) return;
      intentarCerrarRef.current();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, []);

  const guardar = async (e) => {
    e.preventDefault();
    const lista = confirmarCorreoEscrito();
    const encontrados = validarCliente(datos, lista);
    setErrores(encontrados);
    if (encontrados.length) {
      const el = document.getElementById(ID_CAMPO[encontrados[0].campo]);
      if (el) el.focus();
      return;
    }
    setGuardando(true);
    try {
      await onGuardar({
        ...datos,
        nombre: datos.nombre.trim(),
        email: correosAlGuardar(lista),
        telefono: String(datos.telefono || '').replace(/\D/g, ''),
      });
    } catch (err) {
      // Motivo del servidor (4xx, ej. "Email inválido") o, si fue la conexión, qué hacer.
      const motivo = err?.status >= 400 && err?.status < 500 && err.message !== 'Error en la petición' ? err.message : null;
      console.error('No se pudo guardar el cliente:', err);
      setErrores([{ campo: 'servidor', mensaje: motivo, resumen: motivo }]);
    } finally {
      setGuardando(false);
    }
  };

  const error = (campo) => errores.find(x => x.campo === campo);
  const propsError = (campo) => ({
    'aria-invalid': error(campo) ? 'true' : undefined,
    'aria-describedby': [`cf-${campo}-ayuda`, error(campo) && `cf-${campo}-error`].filter(Boolean).join(' '),
  });
  const textoError = (campo) => error(campo) && (
    <span className="cf-error" id={`cf-${campo}-error`}>
      <CircleAlert size={13} aria-hidden="true" /> {error(campo).mensaje}
    </span>
  );
  const servidor = error('servidor');
  const resumen = servidor
    ? `No se guardó: ${(servidor.mensaje || 'revisa tu conexión e intenta de nuevo').replace(/[.\s]*$/, '')}.`
    : resumenErrores(errores);

  return (
    <div className="modal-overlay" onClick={intentarCerrar}>
      <form
        className="modal modal-formulario-cliente"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cf-titulo"
        noValidate
        onSubmit={guardar}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header cf-cabecera">
          <div>
            <h3 id="cf-titulo">{editar ? 'Editar cliente' : 'Nuevo cliente'}</h3>
            <p className="cf-sub">{editar ? inicial.nombre : 'Los datos se pueden cambiar después.'}</p>
          </div>
          <BotonIcono etiqueta="Cerrar" onClick={intentarCerrar}><X size={20} aria-hidden="true" /></BotonIcono>
        </div>

        <div className="cf-cuerpo">
          {errores.length > 0 && resumen && <div className="cf-resumen" role="alert">{resumen}</div>}

          <fieldset>
            <legend>Datos del cliente</legend>
            <div className={`cf-campo${error('nombre') ? ' con-error' : ''}`}>
              <label htmlFor="cf-nombre">Nombre o razón social</label>
              <input
                id="cf-nombre"
                type="text"
                autoComplete="off"
                value={datos.nombre || ''}
                onChange={e => { cambiar('nombre', e.target.value); setSugerencia(null); }}
                onBlur={() => setSugerencia(sugerenciaNombre(datos.nombre))}
                {...propsError('nombre')}
              />
              {textoError('nombre')}
              {sugerencia && (
                <div className="cf-sugerencia" role="status">
                  <span>Está todo en mayúsculas. ¿Lo escribimos así? <b>{sugerencia}</b></span>
                  <span className="cf-sugerencia-acciones">
                    <button
                      type="button"
                      className="cf-btn chico"
                      onClick={() => { cambiar('nombre', sugerirNombre(datos.nombre)); setSugerencia(null); }}
                    >
                      Usar así
                    </button>
                    <button type="button" className="cf-btn chico" onClick={() => setSugerencia(null)}>Dejar como está</button>
                  </span>
                </div>
              )}
            </div>

            <div className="cf-fila">
              <div className="cf-campo">
                <span className="cf-etiqueta" id="cf-tipo-etiqueta">Tipo de persona</span>
                <div className="cf-segmentado" role="radiogroup" aria-labelledby="cf-tipo-etiqueta">
                  {[['moral', 'Moral'], ['fisica', 'Física']].map(([valor, texto]) => (
                    <label key={valor} className={datos.tipo === valor ? 'elegido' : ''}>
                      <input
                        type="radio"
                        name="cf-tipo"
                        value={valor}
                        checked={datos.tipo === valor}
                        onChange={() => cambiar('tipo', valor)}
                      />
                      {texto}
                    </label>
                  ))}
                </div>
              </div>
              <div className={`cf-campo${error('contador') ? ' con-error' : ''}`}>
                <label htmlFor="cf-contador">Contador</label>
                <select
                  id="cf-contador"
                  value={datos.contador_id || ''}
                  onChange={e => cambiar('contador_id', e.target.value)}
                  {...propsError('contador')}
                >
                  <option value="" disabled>Selecciona contador…</option>
                  {responsables.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                </select>
                {textoError('contador')}
              </div>
              <div className={`cf-campo${error('regimen') ? ' con-error' : ''}`}>
                <label htmlFor="cf-regimen">Régimen fiscal</label>
                <select
                  id="cf-regimen"
                  value={datos.regimen || ''}
                  onChange={e => cambiar('regimen', e.target.value)}
                  {...propsError('regimen')}
                >
                  <option value="" disabled>Selecciona régimen…</option>
                  {REGIMENES.map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
                </select>
                {textoError('regimen')}
              </div>
              <div className={`cf-campo${error('industria') ? ' con-error' : ''}`}>
                <label htmlFor="cf-industria">Industria</label>
                <select
                  id="cf-industria"
                  value={datos.industria || ''}
                  onChange={e => cambiar('industria', e.target.value)}
                  {...propsError('industria')}
                >
                  <option value="" disabled>Selecciona industria…</option>
                  {INDUSTRIAS.map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
                </select>
                {textoError('industria')}
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Contacto</legend>
            <div className={`cf-campo${error('correos') ? ' con-error' : ''}`}>
              <label htmlFor="cf-correo">Correos <span className="cf-opcional">(opcional)</span></label>
              <div className="cf-chips" onClick={e => { if (e.target === e.currentTarget) document.getElementById('cf-correo').focus(); }}>
                {correos.map(c => (
                  <span key={c} className={`cf-chip${correoValido(c) ? '' : ' mal'}`}>
                    <span>{c}</span>
                    <BotonIcono
                      etiqueta={`Quitar ${c}`}
                      onClick={() => { cambiarCorreos(correos.filter(x => x !== c)); document.getElementById('cf-correo').focus(); }}
                    >
                      <X size={12} aria-hidden="true" />
                    </BotonIcono>
                  </span>
                ))}
                <input
                  id="cf-correo"
                  type="text"
                  autoComplete="off"
                  inputMode="email"
                  placeholder={correos.length ? '' : 'Escribe un correo y presiona Enter'}
                  value={correoTexto}
                  onChange={e => { setCorreoTexto(e.target.value); setSucio(true); }}
                  onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === ',' || e.key === ';') && correoTexto.trim()) {
                      e.preventDefault();
                      confirmarCorreoEscrito();
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                    } else if (e.key === 'Backspace' && !correoTexto && correos.length) {
                      // Regresa el último chip a texto para corregirlo.
                      e.preventDefault();
                      setCorreoTexto(correos[correos.length - 1]);
                      cambiarCorreos(correos.slice(0, -1));
                    }
                  }}
                  onPaste={e => {
                    const texto = e.clipboardData.getData('text');
                    if (/[,;\s]/.test(texto.trim())) {
                      e.preventDefault();
                      cambiarCorreos(agregarCorreos(correos, `${correoTexto} ${texto}`));
                      setCorreoTexto('');
                    }
                  }}
                  onBlur={confirmarCorreoEscrito}
                  {...propsError('correos')}
                />
              </div>
              <span className="cf-ayuda" id="cf-correos-ayuda">
                A estos correos llegan la solicitud de información y los avisos. Puedes pegar varios separados por coma.
              </span>
              {textoError('correos')}
            </div>
            <div className={`cf-campo cf-corto${error('telefono') ? ' con-error' : ''}`}>
              <label htmlFor="cf-telefono">Teléfono <span className="cf-opcional">(opcional)</span></label>
              <input
                id="cf-telefono"
                type="tel"
                inputMode="numeric"
                value={datos.telefono || ''}
                onChange={e => cambiar('telefono', e.target.value)}
                {...propsError('telefono')}
              />
              <span className="cf-ayuda" id="cf-telefono-ayuda">10 dígitos.</span>
              {textoError('telefono')}
            </div>
          </fieldset>

          <fieldset>
            <legend>Trámites</legend>
            <div className="cf-campo cf-corto">
              <label htmlFor="cf-revision">Revisión de Pedro</label>
              <select
                id="cf-revision"
                value={normalizarRevision(datos.revision_pedro)}
                onChange={e => cambiar('revision_pedro', e.target.value)}
                aria-describedby="cf-revision-ayuda"
              >
                {OPCIONES_REVISION.map(o => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
              </select>
              <span className="cf-ayuda" id="cf-revision-ayuda">
                Pedro revisa la contabilidad y el cálculo juntos; el contador lo marca en Cálculo y sigue con la declaración cuando Pedro lo da por revisado.
              </span>
            </div>
            <div className="cf-campo">
              <span className="cf-etiqueta" id="cf-obligaciones">Obligaciones adicionales</span>
              <div className="cf-opciones" role="group" aria-labelledby="cf-obligaciones">
                {OBLIGACIONES.map(([campo, nombre, detalle]) => (
                  <label key={campo} className="cf-opcion">
                    <input type="checkbox" checked={!!datos[campo]} onChange={e => cambiar(campo, e.target.checked)} />
                    <b>{nombre}</b>
                    <small>{detalle}</small>
                  </label>
                ))}
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend>Configuración del cliente</legend>
            <div className="cf-opciones">
              <label className="cf-opcion">
                <input type="checkbox" checked={!!datos.solo_cobranza} onChange={e => cambiar('solo_cobranza', e.target.checked)} />
                <b>Solo cobranza</b>
                <small>No aparece en la tabla mensual; solo en Cobranza.</small>
              </label>
              <label className="cf-opcion">
                <input type="checkbox" checked={!!datos.otros_ingresos} onChange={e => cambiar('otros_ingresos', e.target.checked)} />
                <b>Otros ingresos</b>
                <small>En Cobranza se muestra en la sección "Otros ingresos".</small>
              </label>
            </div>
          </fieldset>

          {editar && zonaFinal && (
            <fieldset>
              <legend>Desactivar o eliminar</legend>
              {zonaFinal}
            </fieldset>
          )}
        </div>

        <div className="cf-pie">
          <span className="cf-estado" aria-live="polite">{sucio ? 'Cambios sin guardar' : ''}</span>
          <button type="button" className="cf-btn" onClick={intentarCerrar} disabled={guardando}>Cancelar</button>
          <button type="submit" className="cf-btn principal" disabled={guardando} aria-busy={guardando}>
            {guardando ? 'Guardando…' : editar ? 'Guardar cambios' : 'Crear cliente'}
          </button>
        </div>
      </form>
    </div>
  );
}
